import React from "react";
import { formatCurrency } from "./SipCalculatorCalculations";
import { DEFAULT_SIP_CALCULATOR_SYMBOLS, DONUT_COLORS } from "./SipCalculatorConstants";
import type { DonutBreakdownChartProps } from "./SipCalculatorTypes";

const DONUT_TRANSITION_MS = 500;

export const DonutBreakdownChart: React.FC<DonutBreakdownChartProps> = ({
  investedAmount,
  estimatedReturns,
  futureValue,
  rupeeSymbol = DEFAULT_SIP_CALCULATOR_SYMBOLS.rupeeSymbol,
  labels,
}) => {
  const total = Math.max(investedAmount + estimatedReturns, 1);
  const estimatedReturnShare = (estimatedReturns / total) * 100;
  const investedEndShare = estimatedReturnShare + (investedAmount / total) * 100;
  const data = [
    { name: labels.donutEstimatedReturns, value: estimatedReturns },
    { name: labels.donutInvestedAmount, value: investedAmount },
  ];

  return (
    <div
      className="flex flex-col"
      data-cmp="SipCalculatorDonutChart"
      data-cmp-type="react"
      data-cmp-title="SIP Donut Chart"
    >
      <style>{`
        @property --donut-estimated-share {
          syntax: "<percentage>";
          inherits: false;
          initial-value: 0%;
        }
        @property --donut-invested-end-share {
          syntax: "<percentage>";
          inherits: false;
          initial-value: 0%;
        }
        .bfl-sip-donut-chart {
          aspect-ratio: 1 / 1;
          width: clamp(120px, 38%, 204px);
          max-width: 204px;
        }
        @media (min-width: 900px) {
          .bfl-sip-donut-chart {
            width: clamp(160px, 50%, 280px);
            max-width: 280px;
          }
        }
        .bfl-sip-donut-ring {
          background: conic-gradient(
            ${DONUT_COLORS[0]} 0% var(--donut-estimated-share),
            ${DONUT_COLORS[1]} var(--donut-estimated-share) var(--donut-invested-end-share),
            transparent var(--donut-invested-end-share) 100%
          );
          transition:
            --donut-estimated-share ${DONUT_TRANSITION_MS}ms ease-in-out,
            --donut-invested-end-share ${DONUT_TRANSITION_MS}ms ease-in-out;
        }
      `}</style>
      <div className="flex min-w-0 items-start gap-[16px] md:gap-[24px]">
        <div
          className="bfl-sip-donut-chart bfl-sip-donut-ring relative shrink-0 self-start rounded-full"
          style={
            {
              "--donut-estimated-share": `${estimatedReturnShare}%`,
              "--donut-invested-end-share": `${investedEndShare}%`,
            } as React.CSSProperties
          }
        >
          <div className="absolute inset-[17.5%] rounded-full bg-white" />
        </div>

        <div className="font-rubik flex min-w-0 flex-1 flex-col gap-[8px] md:gap-[12px]">
          {data.map((entry, index) => (
            <div key={entry.name} className="flex flex-col">
              <div className="flex items-center gap-1">
                <span
                  className="h-2 w-2 shrink-0"
                  style={{ backgroundColor: DONUT_COLORS[index] }}
                />
                <span className="text-black-normal text-[12px] leading-[16px] font-normal md:text-[14px]">
                  {entry.name}
                </span>
              </div>
              <p className="text-black-normal ml-[12px] text-[12px] leading-[16px] font-medium md:text-[14px]">
                {formatCurrency(entry.value, rupeeSymbol)}
              </p>
            </div>
          ))}

          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <span className="h-2 w-2 shrink-0" style={{ backgroundColor: "transparent" }} />
              <span className="text-black-normal text-[12px] leading-[16px] font-normal md:text-[14px]">
                {labels.donutTotalValue}
              </span>
            </div>
            <p className="text-black-normal ml-[12px] text-[12px] leading-[16px] font-medium md:text-[14px]">
              {formatCurrency(futureValue, rupeeSymbol)}
            </p>
          </div>
        </div>
      </div>

      <div className="text-black-normal font-rubik flex flex-wrap items-center gap-6 pt-3 text-[14px]">
        {data.map((entry, index) => (
          <div key={`${entry.name}-bottom-legend`} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-[1px]"
              style={{ backgroundColor: DONUT_COLORS[index] }}
            />
            <span>{entry.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
